import React from 'react';
import styled, { css } from 'styled-components';
import dynamic from 'next/dynamic';
import { Container, Text } from '@lmn/design-system';
import { BaggageSection, CurrencyFormatterProvider, DictionaryProvider, TransportDetails } from '@lmn/lmn-components';
import { BAGGAGE_LABEL } from '@lmn/lmn-components/components/Trip/shared/baggage/types.js';
import { useTranslation, type TranslationFunction } from '@/app/components/translations/Translation';
import {
  CHECKED_BAGGAGE,
  getCheckedBaggageLabel,
  getHandBaggageLabel,
  getSmallBagLabel,
  HAND_BAGGAGE,
  SMALL_BAG,
} from '@/app/components/hotel-detail/common/luggageTranslations';
import Message from '@/app/components/hotel-detail/Message';
import { isBelgiumTrainsSpecialFare } from '@/app/components/hotel-detail/flight-train/TransportCommon';
import { adaptWay } from '@/app/components/hotel-detail/flight-train/TransportJourneySummary';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import type { BrandedFare } from '@/app/api/hotel-detail/branded-fares/route';
import type {
  BaggageSectionInfo,
  BaggageSectionWay,
} from '@lmn/lmn-components/components/Trip/shared/baggage/types.d.ts';
import { Co2FootPrint } from './Co2FootPrint';
import { IconCheck, IconCross } from '../common/icons';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { FLIGHTS_DATES_FORMAT, getHotelCheckinCheckoutDates } from '../common/dates';
import { BrandedFares } from './BrandedFares';
import TransportTitle from './TransportTitle';

const BelgiumTrainsFareMessage = dynamic(
  () => import(/* webpackChunkName: "BelgiumTrainsFareMessage" */ './BelgiumTrainsFareMessage'),
  { ssr: false }
);

// #region styled components

const JourneyDetailsWrapper = styled.div`
  button {
    color: ${(p) => p.theme.kallisto.color.text.static.textPrimary};
  }

  [class*='TransportDetailsWayIntroSlim__SecondRowContainer'] > span {
    color: ${(p) => p.theme.kallisto.color.text.static.textSecondary};
    margin-left: 0;
  }
`;

const StationHopMessageStyled = styled(Message)`
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundWarningSubtle};
  border-radius: 6px;
  margin: 1rem 1rem 0;
  padding: 1rem;
`;

const BaggageContainer = styled.div`
  margin-top: 16px;

  [class*='BaggageSection__Header'] {
    display: none;
  }

  [class*='Container'] > :nth-child(2) {
    border: 0;
    margin-top: 0;
    padding-top: 0;
  }
`;

const BaggageTitle = styled.div`
  margin-bottom: 12px;
`;

// #endregion

export const StationHopMessage = () => {
  const { t } = useTranslation();

  return (
    <StationHopMessageStyled isDismissable={true}>
      {t('vacation.common.message.trains.stationhop')}
    </StationHopMessageStyled>
  );
};

function isTrainData(type: TransportType): boolean {
  return type === 'TRAIN' || type === 'MIXED';
}

type JourneyDetailsContainerProps = {
  disableBrandedFareSelection?: boolean;
  inbound: TransportBoundType;
  outbound: TransportBoundType;
  co2Footprint: CO2Footprint;
  transportId: string;
  priceIncrement: number;
  eligibleBrandedFares: boolean;
  numberOfBaggagePiecesIncluded: number | null;
  onLoadFare: () => void;
  onSelectBrandedFare?: (brandedFare: BrandedFare | undefined) => void;
};

function JourneyDetailsContainer(props: JourneyDetailsContainerProps) {
  const {
    outbound,
    inbound,
    co2Footprint,
    disableBrandedFareSelection = false,
    transportId,
    priceIncrement,
    eligibleBrandedFares,
    numberOfBaggagePiecesIncluded,
    onLoadFare,
    onSelectBrandedFare,
  } = props;
  const { localization } = usePageWrapperData();
  const { currency, locale } = localization;
  const { t, translationDictionary } = useTranslation();
  const { isTrain, children, adults } = useUrlSearchParams();

  const flightDates = getHotelCheckinCheckoutDates(
    locale,
    outbound.departureDate,
    inbound.departureDate,
    FLIGHTS_DATES_FORMAT
  );

  return (
    <CurrencyFormatterProvider locale={locale} currency={currency} options={{ minimumFractionDigits: 0 }}>
      <DictionaryProvider labels={translationDictionary}>
        <JourneyDetailsWrapper>
          {!isTrain ? (
            <BrandedFares
              context="flight-card"
              outbound={outbound}
              inbound={inbound}
              transportId={transportId}
              priceIncrement={priceIncrement}
              eligibleBrandedFares={eligibleBrandedFares}
              disableBrandedFareSelection={disableBrandedFareSelection}
              numberOfBaggagePiecesIncluded={numberOfBaggagePiecesIncluded}
              onLoadFare={onLoadFare}
              onSelectBrandedFare={onSelectBrandedFare}
              css={css`
                margin-bottom: 16px;
              `}
            />
          ) : undefined}
          <TransportTitle isTrain={isTrain} departureDate={flightDates?.checkin} returnDate={flightDates?.checkout} />
          <TransportDetails
            outbound={adaptWay(outbound, t)}
            inbound={adaptWay(inbound, t)}
            locale={locale}
            seatSelectionMessage={['VS']}
            showBaggageInfo={false}
            isIntroExpandable={true}
            isDefaultCollapsed={false}
            slimIntro={true}
          />
        </JourneyDetailsWrapper>
        {!eligibleBrandedFares && !isTrain && (
          <BaggageContainer>
            <BaggageTitle>
              <Text variant="Title4">{t('widget.trip_card.baggage_widget.included.title')}</Text>
            </BaggageTitle>
            <BaggageSection
              {...adaptBaggage({
                outbound,
                inbound,
                numberOfBaggagePiecesIncluded: numberOfBaggagePiecesIncluded ?? 0,
                numberOfLuggage: adults + children,
                t,
              })}
            />
          </BaggageContainer>
        )}
        {co2Footprint?.co2kg > 0 ? (
          <Co2FootPrint
            co2Footprint={co2Footprint}
            css={`
              margin-top: 16px;
            `}
          />
        ) : null}
      </DictionaryProvider>
    </CurrencyFormatterProvider>
  );
}

export type TransportJourneyDetailsProps = {
  inbound: TransportBoundType;
  outbound: TransportBoundType;
  co2Footprint: CO2Footprint;
  transportId: string;
  eligibleBrandedFares: boolean;
  numberOfBaggagePiecesIncluded: number | null;
  priceIncrement: number;
  onLoadFare: () => void;
  onSelectBrandedFare?: (brandedFare: BrandedFare | undefined) => void;
} & React.HTMLAttributes<HTMLElement>;

export default function TransportJourneyDetails(props: TransportJourneyDetailsProps) {
  const {
    outbound,
    inbound,
    co2Footprint,
    transportId,
    priceIncrement,
    eligibleBrandedFares,
    numberOfBaggagePiecesIncluded,
    onLoadFare,
    onSelectBrandedFare,
    ...rest
  } = props;
  const { isTrain } = useUrlSearchParams();

  return (
    <Container {...rest}>
      {isTrain && (isStopInDifferentStation(outbound.segments) || isStopInDifferentStation(inbound.segments)) && (
        <StationHopMessage />
      )}
      <JourneyDetailsContainer
        inbound={inbound}
        outbound={outbound}
        co2Footprint={co2Footprint}
        transportId={transportId}
        priceIncrement={priceIncrement}
        eligibleBrandedFares={eligibleBrandedFares}
        onLoadFare={onLoadFare}
        onSelectBrandedFare={onSelectBrandedFare}
        numberOfBaggagePiecesIncluded={numberOfBaggagePiecesIncluded}
      />
    </Container>
  );
}

export type TransportJourneyDetailsWithoutCtaProps = {
  transportData: TransportEndpointResponse;
} & React.HTMLAttributes<HTMLElement>;

export function TransportJourneyDetailsWithoutCta(props: TransportJourneyDetailsWithoutCtaProps) {
  const { transportData, ...rest } = props;
  const { outbound, inbound, co2Footprint, type, id, eligibleBrandedFares, numberOfBaggagePiecesIncluded } =
    transportData.transports[0];

  const isTrainModal = isTrainData(type);

  return (
    <Container {...rest}>
      {isTrainModal && (outbound.segments.length > 1 || inbound.segments.length > 1) && <StationHopMessage />}
      {isTrainModal && isBelgiumTrainsSpecialFare(transportData.transports[0] as TransportsType) && (
        <BelgiumTrainsFareMessage
          css={css`
            margin: 1rem 1rem 0;
          `}
        />
      )}
      <JourneyDetailsContainer
        transportId={id}
        priceIncrement={0}
        eligibleBrandedFares={eligibleBrandedFares}
        inbound={inbound}
        outbound={outbound}
        co2Footprint={co2Footprint}
        disableBrandedFareSelection={true}
        onLoadFare={() => null}
        numberOfBaggagePiecesIncluded={numberOfBaggagePiecesIncluded}
      />
    </Container>
  );
}

export function adaptBaggage({
  outbound,
  inbound,
  useCheckIcons,
  numberOfBaggagePiecesIncluded,
  numberOfLuggage,
  t,
}: {
  outbound: TransportBoundType;
  inbound: TransportBoundType;
  useCheckIcons?: boolean;
  numberOfBaggagePiecesIncluded?: number;
  numberOfLuggage?: number;
  t?: TranslationFunction;
}): {
  outbound: BaggageSectionWay;
  inbound?: BaggageSectionWay | undefined;
  notIncluded: BaggageSectionInfo[];
  areBaggageDifferent: boolean;
} {
  const showGroupLuggage = (numberOfBaggagePiecesIncluded ?? 0) > 0;
  const outboundBaggage = getBaggageSectionInfo(
    outbound,
    useCheckIcons,
    showGroupLuggage,
    numberOfBaggagePiecesIncluded,
    numberOfLuggage,
    t
  );
  const inboundBaggage = getBaggageSectionInfo(
    inbound,
    useCheckIcons,
    showGroupLuggage,
    numberOfBaggagePiecesIncluded,
    numberOfLuggage,
    t
  );
  const cheapestBaggagePrice = Math.min(
    inbound.baggageStartingPrice > 0 ? inbound.baggageStartingPrice : outbound.baggageStartingPrice,
    outbound.baggageStartingPrice > 0 ? outbound.baggageStartingPrice : inbound.baggageStartingPrice
  );
  return {
    outbound: {
      isInbound: false,
      departureCity: outbound.segments[0].departureHub.cityName ?? '',
      arrivalCity: outbound.segments[outbound.segments.length - 1].arrivalHub.cityName ?? '',
      baggage: outboundBaggage,
    },
    inbound: {
      isInbound: true,
      departureCity: inbound.segments[0].departureHub.cityName ?? '',
      arrivalCity: inbound.segments[inbound.segments.length - 1].arrivalHub.cityName ?? '',
      baggage: inboundBaggage,
    },
    notIncluded:
      !outbound.includeBaggage && !inbound.includeBaggage && !showGroupLuggage
        ? [
            {
              ...CHECKED_BAGGAGE,
              ...(cheapestBaggagePrice > 0
                ? {
                    priceDescription: {
                      description: 'widget.trip_card.baggage_widget.checked_luggage.price_description',
                      price: cheapestBaggagePrice,
                    },
                  }
                : {}),
              ...(useCheckIcons
                ? {
                    icon: IconCross,
                  }
                : {}),
            },
          ]
        : [],
    areBaggageDifferent:
      outbound.handBaggageIncluded !== inbound.handBaggageIncluded ||
      outbound.includeBaggage !== inbound.includeBaggage,
  };
}

function getBaggageSectionInfo(
  bound: TransportBoundType,
  useCheckIcons?: boolean,
  showGroupLuggage?: boolean,
  numberOfBaggagePiecesIncluded?: number,
  numberOfLuggage?: number,
  t?: TranslationFunction
): BaggageSectionInfo[] {
  return [
    {
      ...SMALL_BAG,
      index: BAGGAGE_LABEL.SMALL_BAG,
      label: getSmallBagLabel({ numberOfLuggage, t }),
      ...(useCheckIcons
        ? {
            icon: IconCheck,
          }
        : {}),
    },
    ...(bound.handBaggageIncluded
      ? [
          {
            ...HAND_BAGGAGE,
            index: BAGGAGE_LABEL.HAND_BAG,
            label: getHandBaggageLabel({ numberOfLuggage, t }),
            ...(useCheckIcons
              ? {
                  icon: IconCheck,
                }
              : {}),
          },
        ]
      : []),
    ...(bound.includeBaggage || showGroupLuggage
      ? [
          {
            ...CHECKED_BAGGAGE,
            index: BAGGAGE_LABEL.CHECKED_BAG,
            ...getCheckedBaggageLabel({
              showGroupLuggage,
              numberOfBaggagePiecesIncluded,
              numberOfLuggage,
              t,
            }),
            ...(useCheckIcons
              ? {
                  icon: IconCheck,
                }
              : {}),
          },
        ]
      : []),
  ];
}

export function isStopInDifferentStation(segments: TransportSegment[]): boolean {
  return segments.some(
    (segment, index) => index < segments.length - 1 && segment.arrivalHub.id !== segments[index + 1].departureHub.id
  );
}
